/**
 * AASB 9 — Financial Instruments (ECL, simplified).
 * Rulepack v1.0.0. Validates a portfolio at the ECL stage level: stage assignment,
 * ECL = PD × LGD × EAD reconciliation, stage-3 credit-impaired flag consistency.
 *
 * Out of scope: hedge accounting, classification cascades, bifurcation, fair-value
 * measurement, modifications, POCI assets — these need much richer inputs and
 * remain AI-interpretive.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parsePercent, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb9-1.0.0';
const fb = findingBuilder('AASB_9' as any, VERSION);

const HMAP = makeHeaderMap([
  ['exposure_id', 'exposure id', 'loan id', 'id'],
  ['counterparty', 'counterparty', 'borrower', 'customer'],
  ['exposure_type', 'exposure type', 'product', 'instrument'],
  ['gross_carrying_amount', 'gross carrying amount', 'gca', 'principal', 'gross balance'],
  ['stage', 'stage', 'ifrs9 stage', 'ecl stage'],
  ['pd', 'pd', 'probability of default'],
  ['lgd', 'lgd', 'loss given default'],
  ['ead', 'ead', 'exposure at default'],
  ['ecl_recognised', 'ecl', 'ecl recognised', 'expected credit loss'],
  ['credit_impaired', 'credit impaired', 'is credit impaired', 'impaired flag'],
  ['days_past_due', 'days past due', 'dpd'],
]);

const REQUIRED = ['exposure_id', 'gross_carrying_amount', 'stage'];

export function detectAASB9(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['ecl', 'pd', 'lgd', 'stage', 'days past due'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB9(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_9' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('exposure_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB9.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const gca = parseNumber(get('gross_carrying_amount'));
    const stageRaw = String(get('stage') ?? '').trim().toLowerCase();
    const stage = stageRaw === '1' || stageRaw === 'stage 1' || stageRaw === 'stage_1' ? 1
                : stageRaw === '2' || stageRaw === 'stage 2' || stageRaw === 'stage_2' ? 2
                : stageRaw === '3' || stageRaw === 'stage 3' || stageRaw === 'stage_3' ? 3
                : null;
    const pd = idx.pd !== undefined ? parsePercent(get('pd')) : null;
    const lgd = idx.lgd !== undefined ? parsePercent(get('lgd')) : null;
    const ead = idx.ead !== undefined ? parseNumber(get('ead')) : null;
    const ecl = idx.ecl_recognised !== undefined ? parseNumber(get('ecl_recognised')) : null;
    const impaired = idx.credit_impaired !== undefined ? parseBool(get('credit_impaired')) : null;
    const dpd = idx.days_past_due !== undefined ? parseNumber(get('days_past_due')) : null;

    if (!(gca !== null && gca >= 0)) { findings.push(fb.build('AASB9.R02', 'error', 'Gross carrying amount cannot be negative', `Exposure ${id}.`, { rowId: id })); return; }
    if (stage === null) { findings.push(fb.build('AASB9.R03', 'error', 'Stage must be 1, 2, or 3', `Exposure ${id}: "${stageRaw}".`, { rowId: id })); return; }

    // R04 PD/LGD bounds
    if (pd !== null && (pd < 0 || pd > 1.001)) findings.push(fb.build('AASB9.R04', 'error', 'PD out of [0, 1] range', `Exposure ${id}: PD ${pd}.`, { rowId: id }));
    if (lgd !== null && (lgd < 0 || lgd > 1.001)) findings.push(fb.build('AASB9.R04', 'error', 'LGD out of [0, 1] range', `Exposure ${id}: LGD ${lgd}.`, { rowId: id }));

    // R05 Stage 3 credit-impaired consistency
    if (stage === 3 && impaired === false) {
      findings.push(fb.build('AASB9.R05', 'error', 'Stage 3 exposure not flagged credit-impaired',
        `Exposure ${id}: stage 3 means credit-impaired (objective evidence of impairment per AASB 9 Appendix A) but credit_impaired flag is false. Reconcile.`,
        { rowId: id }));
    }
    if (stage !== 3 && impaired === true) {
      findings.push(fb.build('AASB9.R05', 'warning', 'Credit-impaired flag without stage 3',
        `Exposure ${id}: credit_impaired=true but stage ${stage}. Should be stage 3.`,
        { rowId: id }));
    }

    // R06 DPD vs stage (rebuttable presumption: 30 days → stage 2; 90 days → stage 3)
    if (dpd !== null) {
      if (dpd >= 90 && stage !== 3) {
        findings.push(fb.build('AASB9.R06', 'warning', 'Days past due ≥90 but stage < 3',
          `Exposure ${id}: ${dpd} dpd. AASB 9 5.5.11 contains a rebuttable 90-day presumption for default; should be stage 3 unless the presumption is rebutted with documented reasoning.`,
          { rowId: id }));
      } else if (dpd >= 30 && stage === 1) {
        findings.push(fb.build('AASB9.R06', 'info', 'Days past due ≥30 but stage 1',
          `Exposure ${id}: ${dpd} dpd. AASB 9 5.5.11 — 30-day rebuttable presumption that credit risk has increased significantly (move to stage 2).`,
          { rowId: id }));
      }
    }

    // R07 ECL recalc (simplified): ECL ≈ PD × LGD × EAD
    if (pd !== null && lgd !== null && ead !== null && ecl !== null) {
      const expectedEcl = pd * lgd * ead;
      const tol = Math.max(50, 0.05 * Math.max(expectedEcl, ecl, 1));
      if (Math.abs(ecl - expectedEcl) > tol) {
        findings.push(fb.build('AASB9.R07', 'warning', 'ECL does not approximate PD × LGD × EAD',
          `Exposure ${id}: reported ECL $${fmt(ecl)}, simple calc $${fmt(expectedEcl)} (PD ${(pd * 100).toFixed(2)}% × LGD ${(lgd * 100).toFixed(2)}% × EAD $${fmt(ead)}). Difference may be intentional (multi-period lifetime ECL for stage 2/3, scenario-weighted PDs); confirm.`,
          { rowId: id, evidence: { reported: ecl, simpleCalc: expectedEcl, pd, lgd, ead } }));
      }
    }

    // R08 EAD vs GCA sanity
    if (ead !== null && ead > gca! * 1.5) {
      findings.push(fb.build('AASB9.R08', 'info', 'EAD materially above gross carrying amount',
        `Exposure ${id}: EAD $${fmt(ead)} > GCA $${fmt(gca!)}. May reflect undrawn commitments or off-balance exposure — confirm scope.`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_9' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
